
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

interface ChatStatus {
  totalMessages: number;
  unreadMessages: number;
  hasMessages: boolean;
}

export const useNotificationChatStatus = (notificationId: string) => {
  const { user } = useAuth();
  
  const { data, isLoading } = useQuery({
    queryKey: ['notification-chat-status', notificationId, user?.id],
    queryFn: async (): Promise<ChatStatus> => {
      // Default status with no messages
      const defaultStatus: ChatStatus = {
        totalMessages: 0,
        unreadMessages: 0,
        hasMessages: false
      };
      
      if (!notificationId) return defaultStatus;
      
      try {
        // Get total messages count
        const { data: totalData, error: totalError } = await supabase
          .from('notification_chat')
          .select('id')
          .eq('notification_id', notificationId);
          
        if (totalError) throw totalError;
        
        const totalMessages = totalData.length;
        
        // If no messages at all, return early
        if (totalMessages === 0) return defaultStatus;
        
        // If we have a user, check for unread messages
        let unreadMessages = 0;
        
        if (user?.id) {
          // Get the last read timestamp for this user and notification
          const { data: readData, error: readError } = await supabase
            .from('notification_chat_read_status')
            .select('last_read_at')
            .eq('notification_id', notificationId)
            .eq('user_id', user.id)
            .maybeSingle();
            
          if (readError) {
            console.error("Error fetching read status:", readError);
          }
          
          // If we have a last read timestamp, count messages after that
          if (readData?.last_read_at) {
            const { data: unreadData, error: unreadError } = await supabase
              .from('notification_chat')
              .select('id')
              .eq('notification_id', notificationId)
              .gt('created_at', readData.last_read_at)
              .not('user_id', 'eq', user.id); // Don't count own messages as unread
              
            if (unreadError) throw unreadError;
            
            unreadMessages = unreadData.length;
          } else {
            // If no read record exists, all messages are unread (except own messages)
            const { data: unreadData, error: unreadError } = await supabase
              .from('notification_chat')
              .select('id')
              .eq('notification_id', notificationId)
              .not('user_id', 'eq', user.id); // Don't count own messages as unread
              
            if (unreadError) throw unreadError;
            
            unreadMessages = unreadData.length;
          }
        }
        
        return {
          totalMessages,
          unreadMessages,
          hasMessages: totalMessages > 0
        };
      } catch (error) {
        console.error("Error fetching chat status:", error);
        return defaultStatus;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchInterval: false, // Use real-time instead of polling
    refetchOnWindowFocus: false, // No refetch on focus
  });
  
  return {
    ...data,
    isLoading
  };
};
